"use client";

import React, { useMemo } from "react";
import ScrollHeader from "@/components/Layouts/ScrollHeader";
import RightSidebar from "@/components/Layouts/RightSidebar";
import FooterContact, { INFO } from "@/components/Layouts/FooterContact";
import HeroSection from "@/components/Sections/HeroSection";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { PROJECT_INFO } from "@/types/projectConsts";
import { parseHtmlTextToJson, stripHTML } from "@/utils/stripHTML";
import StorySplitSection from "@/components/Sections/StorySplitSection";
import StoryGridSection from "@/components/Sections/StoryGridSection";
import ProjectOverviewSection from "@/components/Sections/ProjectOverviewSection";
import FamilyWellnessSection from "@/components/Sections/FamilyWellnessSection";
import ApartmentSpacesSection from "@/components/Sections/ApartmentSpacesSection";
import SettlementSolutionSection from "@/components/Sections/SettlementSolutionSection";
import ConstructionProgressSection from "@/components/Sections/ConstructionProgressSection";
import GallerySection from "@/components/Sections/GallerySection";
import NewsSection from "@/components/Sections/NewsSection";
import DayInLifeSection from "@/components/Sections/DayInLifeSection";
import LocationHighlightSection, {
  LocationData,
} from "@/components/Sections/LocationHighlightSection";
import storyData from "@/data/storyData.json";
import overviewData from "@/data/overviewData.json";
import familyWellnessData from "@/data/familyWellnessData.json";
import apartmentSpacesData from "@/data/apartmentSpacesData.json";
import settlementSolutionData from "@/data/settlementSolutionData.json";
import constructionProgressData from "@/data/constructionProgressData.json";
import newsData from "@/data/newsData.json";
import dayInLifeData from "@/data/dayInLifeData.json";
import locationData from "@/data/locationData.json";
import footerContactData from "@/data/footerContactData.json";
import { useResponsive } from "@/hooks/useResponsive";
import { useFullPageScroll } from "@/hooks/useFullPageScroll";
import { FullPageContext, SectionItem } from "@/context/FullPageContext";

interface HomeProps {
  seo: any;
  data: any;
  locale: string;
}

export default function HomeClient({ data, locale = "vi" }: HomeProps) {
  // Flatten all addon lists to make searching robust across all CMS slots
  const allAddOns = useMemo(
    () => [...(Array.isArray(data?.add_on_data) ? data.add_on_data : [])],
    [data?.add_on_data],
  );

  //#region Data Helper Functions
  const getAddOn = (key: string) => {
    return (
      allAddOns.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };
  const getAddOn2 = (key: string) => {
    return (
      data?.add_on_data_2?.find(
        (item: any) => item?.title?.toLowerCase() === key.toLowerCase(),
      ) || null
    );
  };
  const isSectionShared = (key: string, defaultVal = true): boolean => {
    const item = getAddOn(key);
    if (!item) return defaultVal;
    return (
      item.shared !== false &&
      item.shared !== "false" &&
      item.shared !== 0 &&
      item.shared !== "0"
    );
  };
  const getSectionIndex = (key: string, defaultVal: number): number => {
    const addon = getAddOn(key);
    if (!addon) return defaultVal;
    const stt = addon.stt;
    if (stt === undefined || stt === null || stt === "") {
      return defaultVal;
    }
    const val = Number(stt);
    return isNaN(val) ? defaultVal : val;
  };
  //#endregion

  //#region Dynamic Project Info Configuration
  const projectInfo = data?.content;
  if (projectInfo) {
    try {
      const cleanContent = stripHTML(projectInfo).trim();
      const parsed = JSON.parse(cleanContent);
      if (parsed.projectName) PROJECT_INFO.projectName = parsed.projectName;
      if (parsed.address) PROJECT_INFO.address = parsed.address;
      if (parsed.hotline) PROJECT_INFO.hotline = parsed.hotline;
      if (parsed.email) PROJECT_INFO.email = parsed.email;
      if (parsed.website) PROJECT_INFO.website = parsed.website;
      if (parsed.zalo) PROJECT_INFO.zalo = parsed.zalo;
      if (parsed.messenger) {
        PROJECT_INFO.messenger = parsed.messenger;
        PROJECT_INFO.fb = parsed.messenger;
      } else if (parsed.fb) {
        PROJECT_INFO.messenger = parsed.fb;
        PROJECT_INFO.fb = parsed.fb;
      }
    } catch (e) {
      console.error("Failed to parse project_info JSON:", e);
    }
  }
  //#endregion

  //#region Hero Section Data
  const heroItem = getAddOn("herosection");
  const heroFormAddon = data?.add_on_data_2?.find(
    (item: any) => item?.title?.toLowerCase() === "hero-form",
  );
  const showHeroForm = heroFormAddon?.shared !== false;

  const heroImages = appendImageUrlFromAPI({
    src: heroItem?.picture || "/images/hero_skyline.png",
    size: "2k",
  });

  const heroSlidePictures = [
    heroItem?.picture,
    heroItem?.picture_2,
    heroItem?.picture_3,
    heroItem?.picture_4,
    heroItem?.picture_5,
    heroItem?.picture_6,
  ]
    .filter(Boolean)
    .map((src: string) => appendImageUrlFromAPI({ src, size: "2k" }));

  let heroTitle = heroItem?.summary || "Sống trọn giữa\ntâm điểm kết nối";
  let heroDes =
    heroItem?.content_2 ||
    "Nơi công việc, gia đình, thiên nhiên và tương lai cùng hội tụ, mở ra một cuộc sống trọn vẹn hơn mỗi ngày.";
  let heroSubTitle = "MỸ HẠNH · TÂY NINH · GIÁP TP.HCM";
  let heroButtonText = "KHÁM PHÁ DRAGON HOME";
  let heroWatermark1 = "Live Connected";
  let heroWatermark2 = "Live Well";
  let heroSlideOrVideo = "slide";
  let heroVideoUrl = "";

  try {
    const rawContent = heroItem?.content
      ? stripHTML(heroItem.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed.title) heroTitle = parsed.title;
      if (parsed.subTitle || parsed.heroSubTitle)
        heroSubTitle = parsed.subTitle || parsed.heroSubTitle;
      if (parsed.description || parsed.heroDes)
        heroDes = parsed.description || parsed.heroDes;
      if (parsed.buttonText || parsed.heroButtonText)
        heroButtonText = parsed.buttonText || parsed.heroButtonText;
      if (parsed.watermark1 || parsed.heroWatermark1)
        heroWatermark1 = parsed.watermark1 || parsed.heroWatermark1;
      if (parsed.watermark2 || parsed.heroWatermark2)
        heroWatermark2 = parsed.watermark2 || parsed.heroWatermark2;
      if (parsed.slideorvideo) heroSlideOrVideo = parsed.slideorvideo;
      if (parsed.videourl || parsed.videoUrl)
        heroVideoUrl = parsed.videourl || parsed.videoUrl;
    }
  } catch (e) {
    console.error("Failed to parse hero options:", e);
  }
  //#endregion

  //#region Story Section Data
  const storyItem = getAddOn("cau-chuyen-thuong-hieu");
  const storyItem2 = getAddOn2("cau-chuyen-thuong-hieu");
  let dynamicStoryData = {
    screens: storyData.screens,
    screen4: storyData.screen4,
  };

  try {
    const rawContent = storyItem?.content
      ? stripHTML(storyItem.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed && Array.isArray(parsed.screens)) {
        const apiImages = [
          storyItem?.picture
            ? appendImageUrlFromAPI({ src: storyItem.picture, size: "2k" })
            : null,
          storyItem?.picture_2
            ? appendImageUrlFromAPI({ src: storyItem.picture_2, size: "2k" })
            : null,
          storyItem?.picture_3
            ? appendImageUrlFromAPI({ src: storyItem.picture_3, size: "2k" })
            : null,
        ];

        const mappedScreens = parsed.screens.map((screen: any, idx: number) => {
          const imgUrl = screen.image ? screen.image : apiImages[idx] || "";
          const vUrl = screen.videourl || screen.videoUrl || "";
          return {
            ...screen,
            image: imgUrl,
            videourl: vUrl,
          };
        });

        dynamicStoryData.screens =
          mappedScreens.length > 0 ? mappedScreens : storyData.screens;

        if (parsed.screen4) {
          dynamicStoryData.screen4 = {
            ...storyData.screen4,
            ...parsed.screen4,
          };
        }
      }
    }
  } catch (e) {
    console.error("Failed to parse story options:", e);
  }

  // Process add_on_data_2 (storyItem2) for screen4 cards grid
  try {
    const targetItem = storyItem2 || storyItem;
    const rawContent2 = targetItem?.content
      ? stripHTML(targetItem.content).trim()
      : "";
    if (rawContent2) {
      const parsed2 = JSON.parse(rawContent2);

      const apiPictures = [
        targetItem?.picture
          ? appendImageUrlFromAPI({ src: targetItem.picture, size: "2k" })
          : null,
        targetItem?.picture_2
          ? appendImageUrlFromAPI({ src: targetItem.picture_2, size: "2k" })
          : null,
        targetItem?.picture_3
          ? appendImageUrlFromAPI({ src: targetItem.picture_3, size: "2k" })
          : null,
        targetItem?.picture_4
          ? appendImageUrlFromAPI({ src: targetItem.picture_4, size: "2k" })
          : null,
        targetItem?.picture_5
          ? appendImageUrlFromAPI({ src: targetItem.picture_5, size: "2k" })
          : null,
      ];

      if (Array.isArray(parsed2)) {
        const mappedCards = parsed2.map((cardItem: any, idx: number) => ({
          ...cardItem,
          picture:
            cardItem.picture || cardItem.image || apiPictures[idx] || undefined,
        }));

        dynamicStoryData.screen4 = {
          ...dynamicStoryData.screen4,
          cards: mappedCards,
        };
      } else if (parsed2 && Array.isArray(parsed2.cards)) {
        const mappedCards = parsed2.cards.map((cardItem: any, idx: number) => ({
          ...cardItem,
          picture:
            cardItem.picture || cardItem.image || apiPictures[idx] || undefined,
        }));

        dynamicStoryData.screen4 = {
          ...dynamicStoryData.screen4,
          ...parsed2,
          cards: mappedCards,
        };
      }
    }
  } catch (e) {
    console.error("Failed to parse story add_on_data_2:", e);
  }
  //#endregion

  //#region Overview Section Data
  const overviewItem = getAddOn("tong-quan");
  let dynamicOverviewData = {
    left: {
      image: "",
      tag: "",
      title: "",
      stats: [] as any[],
    },
    right: {
      sectionIndex: "",
      totalSections: "",
      tag: "",
      title: "",
      description: "",
      specs: [] as any[],
      ctaText: "",
      ctaHref: "",
      disclaimer: "",
    },
  };

  try {
    const rawContent = overviewItem?.content
      ? stripHTML(overviewItem.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed) {
        const apiImage = overviewItem?.picture
          ? appendImageUrlFromAPI({ src: overviewItem.picture, size: "2k" })
          : null;

        dynamicOverviewData = {
          left: {
            ...(parsed.left || {}),
            image: parsed.left?.image || apiImage || "",
          },
          right: {
            ...(parsed.right || {}),
          },
        };
      }
    }
  } catch (e) {
    console.error("Failed to parse overview options:", e);
  }

  //#endregion

  //#region Family Wellness Section Data
  const familyWellnessItem =
    getAddOn("he-sinh-thai-song-khoe") || getAddOn("gia-dinh-song-khoe");
  let dynamicFamilyWellnessData = familyWellnessData as any;

  try {
    const rawContent = familyWellnessItem?.content
      ? stripHTML(familyWellnessItem.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed) {
        const apiCardImages = [
          familyWellnessItem?.picture
            ? appendImageUrlFromAPI({
                src: familyWellnessItem.picture,
                size: "2k",
              })
            : null,
          familyWellnessItem?.picture_2
            ? appendImageUrlFromAPI({
                src: familyWellnessItem.picture_2,
                size: "2k",
              })
            : null,
          familyWellnessItem?.picture_3
            ? appendImageUrlFromAPI({
                src: familyWellnessItem.picture_3,
                size: "2k",
              })
            : null,
        ];

        const mappedCards = (parsed.right?.cards || []).map(
          (card: any, idx: number) => {
            const cardImg = card.image || apiCardImages[idx] || "";
            return {
              ...card,
              image: cardImg,
            };
          },
        );

        dynamicFamilyWellnessData = {
          ...familyWellnessData,
          left: {
            ...familyWellnessData.left,
            ...(parsed.left || {}),
            image:
              parsed.left?.image ||
              apiCardImages[0] ||
              familyWellnessData.left.image,
          },
          right: {
            ...familyWellnessData.right,
            ...(parsed.right || {}),
            cards:
              mappedCards.length > 0
                ? mappedCards
                : familyWellnessData.right.cards,
          },
        };
      }
    }
  } catch (e) {
    console.error("Failed to parse family wellness options:", e);
  }
  //#endregion

  //#region Location Section Data
  const locationItem = getAddOn("vi-tri-ket-noi");
  let dynamicLocationData: LocationData = locationData as LocationData;

  try {
    const rawContent = locationItem?.content
      ? stripHTML(locationItem.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed) {
        const apiImage = locationItem?.picture
          ? appendImageUrlFromAPI({ src: locationItem.picture, size: "2k" })
          : null;

        dynamicLocationData = {
          ...locationData,
          ...parsed,
          mapImage: apiImage || parsed.mapImage || locationData.mapImage,
        };
      }
    }
  } catch (e) {
    console.error("Failed to parse location options:", e);
  }

  //#endregion

  //#region Day In Life Section Data (Mot Ngay Song Tron)
  const dayItem = getAddOn("mot-ngay-song-tron") || getAddOn("mot-ngay-song");
  let dynamicDayData = dayInLifeData as any;

  try {
    let generalParsed: any = {};
    const rawContent = dayItem?.content
      ? stripHTML(dayItem.content).trim()
      : "";
    if (rawContent) {
      try {
        generalParsed = JSON.parse(rawContent);
      } catch (e) {}
    }

    // Source 2: Tabs Data ("mot-ngay-song-tron-mat-bang")
    const matBangItems = (data?.add_on_data_2 || []).filter(
      (item: any) =>
        item?.title?.toLowerCase() === "mot-ngay-song-tron-mat-bang" &&
        item?.shared !== false &&
        item?.shared !== "false",
    );

    let parsedTabs: any[] = [];
    if (matBangItems.length > 0) {
      parsedTabs = matBangItems.map((record: any, idx: number) => {
        const pics = [
          record.picture,
          record.picture_2,
          record.picture_3,
          record.picture_4,
          record.picture_5,
          record.picture_6,
        ].filter(Boolean);

        let gallery: any[] = pics.map((pic: string) => ({
          type: "image",
          src: appendImageUrlFromAPI({ src: pic, size: "2k" }),
          thumb: appendImageUrlFromAPI({ src: pic, size: "s" }),
          title: record.summary || `Mặt bằng ${idx + 1}`,
        }));

        let recordContentParsed: any = null;
        try {
          const raw = stripHTML(record.content || "").trim();
          if (raw) recordContentParsed = JSON.parse(raw);
        } catch (e) {}

        if (
          recordContentParsed?.video &&
          Array.isArray(recordContentParsed.video)
        ) {
          const videoItems = recordContentParsed.video.map((v: any) => ({
            type: "video",
            src: v.url,
            title: record.summary || `Video mặt bằng ${idx + 1}`,
          }));
          gallery = [...gallery, ...videoItems];
        }

        return {
          id: String(idx + 1).padStart(2, "0"),
          label: record.summary || `TẦNG ${idx + 1}`,
          active: idx === 0,
          gallery,
        };
      });
    }

    // Source 3: Cards Timeline Data ("mot-ngay-song-tron-timeline")
    const timelineItems = (data?.add_on_data_2 || []).filter(
      (item: any) =>
        item?.title?.toLowerCase() === "mot-ngay-song-tron-timeline" &&
        item?.shared !== false &&
        item?.shared !== "false",
    );

    let parsedCards: any[] = [];
    if (timelineItems.length > 0) {
      parsedCards = timelineItems.map((record: any, idx: number) => {
        let cardParsed: any = {};
        try {
          const raw = stripHTML(record.content || "").trim();
          if (raw) cardParsed = JSON.parse(raw);
        } catch (e) {}

        const cardImg = record.picture
          ? appendImageUrlFromAPI({ src: record.picture, size: "2k" })
          : cardParsed.image || "";

        const pics = [
          record.picture,
          record.picture_2,
          record.picture_3,
          record.picture_4,
          record.picture_5,
          record.picture_6,
        ]
          .filter((p: any) =>
            Boolean(p && typeof p === "string" && p.trim() !== ""),
          )
          .map((src: string) => appendImageUrlFromAPI({ src, size: "2k" }));

        const images =
          pics.length > 0
            ? pics
            : cardParsed.images || (cardImg ? [cardImg] : []);

        return {
          id: cardParsed.id || `c${idx + 1}`,
          time: cardParsed.time || "",
          subtag: cardParsed.subtag || "",
          title: cardParsed.title || "",
          footer: cardParsed.footer || "",
          videourl: cardParsed.videourl || cardParsed.videoUrl || "",
          image: cardImg,
          images: images,
          picture: record.picture
            ? appendImageUrlFromAPI({ src: record.picture, size: "2k" })
            : undefined,
          picture_2: record.picture_2
            ? appendImageUrlFromAPI({ src: record.picture_2, size: "2k" })
            : undefined,
          picture_3: record.picture_3
            ? appendImageUrlFromAPI({ src: record.picture_3, size: "2k" })
            : undefined,
          picture_4: record.picture_4
            ? appendImageUrlFromAPI({ src: record.picture_4, size: "2k" })
            : undefined,
          picture_5: record.picture_5
            ? appendImageUrlFromAPI({ src: record.picture_5, size: "2k" })
            : undefined,
          picture_6: record.picture_6
            ? appendImageUrlFromAPI({ src: record.picture_6, size: "2k" })
            : undefined,
        };
      });
    }

    dynamicDayData = {
      ...dayInLifeData,
      ...generalParsed,
      tag: generalParsed.tag || dayInLifeData.tag,
      title: generalParsed.title || dayInLifeData.title,
      description: generalParsed.description || dayInLifeData.description,
      filterLabel: generalParsed.filterLabel || dayInLifeData.filterLabel,
      video: generalParsed.video || dayInLifeData.video,
      tabs: parsedTabs.length > 0 ? parsedTabs : dayInLifeData.tabs,
      cards: parsedCards.length > 0 ? parsedCards : dayInLifeData.cards,
    };
  } catch (e) {
    console.error("Failed to parse day in life options:", e);
  }
  //#endregion

  //#region Apartment Spaces Section Data (Khong Gian Song)

  const generalSpacesAddon = getAddOn("khong-gian-song");
  const spacesRecords = (data?.add_on_data_2 || []).filter(
    (item: any) =>
      item?.title?.toLowerCase() === "khong-gian-song" &&
      item?.shared !== false &&
      item?.shared !== "false",
  );

  let dynamicApartmentSpacesData: any = null;

  try {
    if (generalSpacesAddon?.content) {
      try {
        const raw = stripHTML(generalSpacesAddon.content).trim();
      } catch (e) {}
    }

    const tag = generalSpacesAddon?.content_3 || "05 · KHÔNG GIAN SỐNG";
    const title =
      generalSpacesAddon?.summary ||
      "Vừa vặn hôm nay.\nLinh hoạt cho ngày mai.";
    const description = generalSpacesAddon?.content
      ? stripHTML(generalSpacesAddon.content).trim()
      : "";
    const apartmentRecords = spacesRecords.filter((rec: any) => {
      const raw = stripHTML(rec.content || "").trim();
      return raw.startsWith("{");
    });

    let parsedApartments: any[] = [];
    if (apartmentRecords.length > 0) {
      parsedApartments = apartmentRecords.map((rec: any, idx: number) => {
        let aptParsed: any = {};
        try {
          const raw = stripHTML(rec.content || "").trim();
          if (raw) aptParsed = JSON.parse(raw);
        } catch (e) {}

        const floorPlanImage = rec.picture
          ? appendImageUrlFromAPI({ src: rec.picture, size: "2k" })
          : aptParsed.floorPlanImage || "";

        const sliderPics = [
          rec.picture_2,
          rec.picture_3,
          rec.picture_4,
          rec.picture_5,
          rec.picture_6,
        ]
          .filter(Boolean)
          .map((src: string) => appendImageUrlFromAPI({ src, size: "2k" }));

        const images =
          sliderPics.length > 0
            ? sliderPics
            : floorPlanImage
              ? [floorPlanImage]
              : [];

        return {
          id: aptParsed.id || `apt-${idx + 1}`,
          tabLabel: aptParsed.tabLabel || aptParsed.name || `Căn ${idx + 1}`,
          name: aptParsed.name || aptParsed.tabLabel || `Căn ${idx + 1}`,
          indexNumber:
            aptParsed.indexNumber || String(idx + 1).padStart(2, "0"),
          indexLabel:
            aptParsed.indexLabel ||
            `${String(idx + 1).padStart(2, "0")} / ${String(apartmentRecords.length).padStart(2, "0")}`,
          headline: aptParsed.headline || "",
          subDescription: aptParsed.subDescription || "",
          imageTag: aptParsed.imageTag || "",
          imageQuote: aptParsed.imageQuote || "",
          floorPlanImage: floorPlanImage,
          images: images,
          mainImage: images[0] || floorPlanImage,
        };
      });
    }

    if (spacesRecords.length > 0 || generalSpacesAddon) {
      dynamicApartmentSpacesData = {
        tag,
        title,
        description,
        apartments: parsedApartments,
      };
    }
  } catch (e) {
    console.error("Failed to parse apartment spaces options:", e);
  }

  //#endregion

  //#region Settlement Solution Section Data (Giai Phap An Cu)
  const solutionAddon = getAddOn("giai-phap-an-cu");
  let dynamicSettlementSolutionData: any = null;

  try {
    if (solutionAddon?.content) {
      const rawContent = stripHTML(solutionAddon.content).trim();
      if (rawContent) {
        try {
          const parsed = JSON.parse(rawContent);
          if (parsed) {
            dynamicSettlementSolutionData = {
              ...parsed,
              tag: parsed.tag || solutionAddon.content_3 || "",
              title: parsed.title || solutionAddon.summary || "",
              description: parsed.description || "",
            };
          }
        } catch (e) {
          console.error("Failed to parse settlement solution content JSON:", e);
        }
      }
    }
  } catch (e) {
    console.error("Failed to parse settlement solution options:", e);
  }

  //#endregion

  //#region Construction Progress Section Data (Hanh Trinh Kien Tao)
  const progressAddon = getAddOn("hanh-trinh-kien-tao");
  const progressMilestoneRecords = (data?.add_on_data_2 || []).filter(
    (item: any) =>
      item?.title?.toLowerCase() === "hanh-trinh-kien-tao" &&
      item?.shared !== false &&
      item?.shared !== "false",
  );

  let dynamicConstructionProgressData: any = null;

  try {
    let generalParsed: any = {};
    const rawContent = progressAddon?.content
      ? stripHTML(progressAddon.content).trim()
      : "";

    if (rawContent.startsWith("{")) {
      try {
        generalParsed = JSON.parse(rawContent);
      } catch (e) {}
    }

    const tag =
      progressAddon?.content_2 ||
      progressAddon?.content_3 ||
      generalParsed.tag ||
      "07 · HÀNH TRÌNH KIẾN TẠO";

    const title =
      progressAddon?.summary ||
      generalParsed.title ||
      "Từng ngày hình thành\nmột nơi để trở về";

    const description =
      (!rawContent.startsWith("{") ? rawContent : "") ||
      generalParsed.description ||
      "";

    let parsedMilestones: any[] = [];
    if (progressMilestoneRecords.length > 0) {
      parsedMilestones = progressMilestoneRecords.map(
        (rec: any, idx: number) => {
          let milestoneParsed: any = {};
          try {
            const raw = stripHTML(rec.content || "").trim();
            if (raw) milestoneParsed = JSON.parse(raw);
          } catch (e) {}

          const milestoneImg = rec.picture
            ? appendImageUrlFromAPI({ src: rec.picture, size: "2k" })
            : milestoneParsed.image
              ? appendImageUrlFromAPI({
                  src: milestoneParsed.image,
                  size: "2k",
                })
              : "";

          const pics = [
            rec.picture,
            rec.picture_2,
            rec.picture_3,
            rec.picture_4,
            rec.picture_5,
            rec.picture_6,
          ]
            .filter((p: any) =>
              Boolean(p && typeof p === "string" && p.trim() !== ""),
            )
            .map((src: string) => appendImageUrlFromAPI({ src, size: "2k" }));

          const images =
            pics.length > 0
              ? pics
              : milestoneParsed.images || (milestoneImg ? [milestoneImg] : []);

          return {
            id: milestoneParsed.id || `milestone-${idx + 1}`,
            index: milestoneParsed.index || String(idx + 1).padStart(2, "0"),
            status: milestoneParsed.status || rec.summary || "",
            title: milestoneParsed.title || rec.summary || "",
            image: milestoneImg,
            images: images,
            picture: rec.picture
              ? appendImageUrlFromAPI({ src: rec.picture, size: "2k" })
              : undefined,
            picture_2: rec.picture_2
              ? appendImageUrlFromAPI({ src: rec.picture_2, size: "2k" })
              : undefined,
            picture_3: rec.picture_3
              ? appendImageUrlFromAPI({ src: rec.picture_3, size: "2k" })
              : undefined,
            picture_4: rec.picture_4
              ? appendImageUrlFromAPI({ src: rec.picture_4, size: "2k" })
              : undefined,
            picture_5: rec.picture_5
              ? appendImageUrlFromAPI({ src: rec.picture_5, size: "2k" })
              : undefined,
            picture_6: rec.picture_6
              ? appendImageUrlFromAPI({ src: rec.picture_6, size: "2k" })
              : undefined,
            imageOverlayTag: milestoneParsed.imageOverlayTag || "",
            imageOverlayTitle: milestoneParsed.imageOverlayTitle || "",
          };
        },
      );
    }

    if (progressAddon || progressMilestoneRecords.length > 0) {
      dynamicConstructionProgressData = {
        ...constructionProgressData,
        ...generalParsed,
        tag,
        title,
        description,
        milestones:
          parsedMilestones.length > 0
            ? parsedMilestones
            : constructionProgressData.milestones,
      };
    }
  } catch (e) {
    console.error("Failed to parse construction progress options:", e);
  }

  //#endregion

  //#region Gallery Section Data (Thu Vien)
  const galleryAddon = getAddOn("thu-vien");
  const galleryRecords = (data?.add_on_data_2 || []).filter(
    (item: any) =>
      item?.title?.toLowerCase() === "thu-vien" &&
      item?.shared !== false &&
      item?.shared !== "false",
  );

  let dynamicGalleryData: any = null;

  try {
    const tag =
      galleryAddon?.summary ||
      galleryAddon?.content_3 ||
      "THƯ VIỆN DRAGON HOME";

    const title =
      galleryAddon?.content_2 || "Những lát cắt của một đời sống kết nối";

    const description =
      (galleryAddon?.content && !galleryAddon.content.startsWith("<p><br>")
        ? stripHTML(galleryAddon.content).trim()
        : "") || "";

    const ctaLink = {
      label: "XEM TOÀN BỘ THƯ VIỆN",
      url: "#",
    };

    let parsedCards: any[] = [];
    if (galleryRecords.length > 0) {
      parsedCards = galleryRecords.map((rec: any, idx: number) => {
        const typeStr = (rec.content_3 || "").toLowerCase();
        const cardType: "image" | "video" = typeStr.includes("video")
          ? "video"
          : "image";

        const pics = [
          rec.picture,
          rec.picture_2,
          rec.picture_3,
          rec.picture_4,
          rec.picture_5,
          rec.picture_6,
        ].filter(Boolean);

        let contentParsed: any = null;
        try {
          const raw = stripHTML(rec.content || "").trim();
          if (raw) {
            if (raw.startsWith("{")) {
              contentParsed = JSON.parse(raw);
            } else {
              const match = raw.match(/\{[\s\S]*\}/);
              if (match) contentParsed = JSON.parse(match[0]);
            }
          }
        } catch (e) {}

        const videoUrl =
          contentParsed?.videoUrl ||
          contentParsed?.url ||
          (typeof contentParsed?.video === "string"
            ? contentParsed.video
            : contentParsed?.video?.[0]?.url) ||
          "";

        let mediaList: any[] = [];

        if (cardType === "video") {
          if (videoUrl) {
            mediaList.push({
              type: "video",
              src: videoUrl,
              thumb: rec.picture
                ? appendImageUrlFromAPI({ src: rec.picture, size: "2k" })
                : "",
              title: rec.summary || rec.content_2 || "",
            });
          }
          pics.forEach((pic: string) => {
            mediaList.push({
              type: "image",
              src: appendImageUrlFromAPI({ src: pic, size: "2k" }),
              thumb: appendImageUrlFromAPI({ src: pic, size: "s" }),
              title: rec.summary || rec.content_2 || "",
            });
          });
        } else {
          mediaList = pics.map((pic: string) => ({
            type: "image",
            src: appendImageUrlFromAPI({ src: pic, size: "2k" }),
            thumb: appendImageUrlFromAPI({ src: pic, size: "s" }),
            title: rec.summary || rec.content_2 || "",
          }));
        }

        const thumbImg = rec.picture
          ? appendImageUrlFromAPI({ src: rec.picture, size: "2k" })
          : mediaList[0]?.thumb || mediaList[0]?.src || "";

        return {
          id: rec._id
            ? `gallery-${rec._id}-${idx}`
            : rec.__id_tmp__
              ? `gallery-${rec.__id_tmp__}-${idx}`
              : `gallery-card-${idx + 1}`,
          tag: rec.content_2 || rec.summary || "",
          title: rec.summary || rec.content_2 || "",
          type: cardType,
          thumb: thumbImg,
          media: mediaList,
        };
      });
    }

    if (galleryAddon || galleryRecords.length > 0) {
      dynamicGalleryData = {
        tag,
        title,
        description,
        ctaLink,
        items: parsedCards,
      };
    }
  } catch (e) {
    console.error("Failed to parse gallery options:", e);
  }
  //#endregion

  //#region News Section Data
  const newsAddon =
    getAddOn("tin-tuc") ||
    getAddOn("news-section") ||
    getAddOn("tin-tuc-su-kien");
  let dynamicNewsData = newsData as any;

  try {
    const rawContent = newsAddon?.content
      ? stripHTML(newsAddon.content).trim()
      : "";
    if (rawContent) {
      const parsed = JSON.parse(rawContent);
      if (parsed) {
        dynamicNewsData = {
          ...newsData,
          ...parsed,
          tag: parsed.tag || newsAddon.content_3 || newsData.tag,
          title: parsed.title || newsAddon.summary || newsData.title,
          subtitle:
            parsed.subtitle || newsAddon.content_2 || newsData.subtitle,
        };
      }
    }
  } catch (e) {
    console.error("Failed to parse news options:", e);
  }
  //#endregion

  //#region Footer Data
  const footerItem = getAddOn("footer");

  const partnersSection = data?.add_on_data_2?.find(
    (item: any) => item?.title?.toLowerCase() === "footer-partners",
  );

  const dynamicFooterContactData = useMemo(() => {
    let baseData: any = {};
    if (footerItem?.content) {
      const parsed = parseHtmlTextToJson<any>(footerItem.content);
      baseData = Array.isArray(parsed) ? parsed[0] || {} : parsed || {};
    }

    const projectLogoObj =
      baseData.projectLogo || footerContactData.middleColumn.projectLogo;
    const projectLogoSrc = footerItem?.picture
      ? appendImageUrlFromAPI({ src: footerItem.picture, size: "xl" })
      : "";

    const dev1Src = footerItem?.picture_2
      ? appendImageUrlFromAPI({ src: footerItem.picture_2, size: "xl" })
      : "";
    const dev2Src = footerItem?.picture_3
      ? appendImageUrlFromAPI({ src: footerItem.picture_3, size: "xl" })
      : "";

    const rawDevs =
      baseData.developers || footerContactData.middleColumn.developers || [];
    const developers = rawDevs.map((dev: any, idx: number) => ({
      role: dev.role || (idx === 0 ? "CHỦ ĐẦU TƯ" : "NHÀ PHÁT TRIỂN"),
      name: dev.name || "",
      src:
        (idx === 0
          ? dev1Src || dev.src
          : idx === 1
            ? dev2Src || dev.src
            : dev.src) || "",
    }));

    // Partner addon 2 record
    const partnerRecord = data?.add_on_data_2?.find((item: any) => {
      const t = item?.title?.toLowerCase()?.trim();
      return t === "footer-partner" || t === "footer-partners";
    });

    let partnerJson: any = {};
    if (partnerRecord?.content) {
      const parsed = parseHtmlTextToJson<any>(partnerRecord.content);
      partnerJson = Array.isArray(parsed) ? parsed[0] || {} : parsed || {};
    }

    const partnerLogos = [
      partnerRecord?.picture,
      partnerRecord?.picture_2,
      partnerRecord?.picture_3,
      partnerRecord?.picture_4,
      partnerRecord?.picture_5,
      partnerRecord?.picture_6,
    ]
      .filter(Boolean)
      .map((src) => appendImageUrlFromAPI({ src, size: "s" }));

    const rawPartners =
      partnerJson.partners || footerContactData.middleColumn.partners || [];
    const partners = rawPartners.map((p: any, idx: number) => ({
      name: p.name || `Đối tác ${idx + 1}`,
      src: partnerLogos[idx] || p.src || "",
    }));

    return {
      tag: baseData.tag || footerContactData.tag,
      title: baseData.title || footerContactData.title,
      description: baseData.description || footerContactData.description,
      hotlineLabel: baseData.hotlineLabel || footerContactData.hotlineLabel,
      hotlineValue: baseData.hotlineValue || footerContactData.hotlineValue,
      emailLabel: baseData.emailLabel || footerContactData.emailLabel,
      emailValue: baseData.emailValue || footerContactData.emailValue,
      middleColumn: {
        developersTitle:
          baseData.developersTitle ||
          footerContactData.middleColumn.developersTitle,
        projectLogo: {
          name: projectLogoObj.name || "DRAGON HOME",
          src: projectLogoSrc,
        },
        developers,
        partnersTitle:
          partnerJson.partnersTitle ||
          footerContactData.middleColumn.partnersTitle,
        partners,
      },
      formTitle: baseData.formTitle || footerContactData.formTitle,
      formSecurityNote:
        baseData.formSecurityNote || footerContactData.formSecurityNote,
      formSuccessTitle:
        baseData.formSuccessTitle || footerContactData.formSuccessTitle,
      formSuccessDesc:
        baseData.formSuccessDesc || footerContactData.formSuccessDesc,
      nameLabel: baseData.nameLabel || footerContactData.nameLabel,
      namePlaceholder:
        baseData.namePlaceholder || footerContactData.namePlaceholder,
      phoneLabel: baseData.phoneLabel || footerContactData.phoneLabel,
      phonePlaceholder:
        baseData.phonePlaceholder || footerContactData.phonePlaceholder,
      needLabel: baseData.needLabel || footerContactData.needLabel,
      needOptions: baseData.needOptions || footerContactData.needOptions,
      formFooterNote:
        baseData.formFooterNote || footerContactData.formFooterNote,
      submitBtnText: baseData.submitBtnText || footerContactData.submitBtnText,
      submittingBtnText:
        baseData.submittingBtnText || footerContactData.submittingBtnText,
    };
  }, [footerItem, data?.add_on_data_2]);
  //#endregion

  //#region FullPage Sections Construct
  const activeSections: SectionItem[] = useMemo(() => {
    type RawSectionItem = SectionItem & {
      index: number;
      shared: boolean;
    };
    const list: RawSectionItem[] = [];

    // 1. HERO SECTION
    const heroShared = isSectionShared("herosection");
    if (heroShared) {
      list.push({
        id: "banner-anchor",
        label: "Trang chủ",
        hideDotNav: false,
        transparent: true,
        element: (
          <HeroSection
            imageUrl={heroImages}
            slideImages={
              heroSlidePictures.length > 0 ? heroSlidePictures : [heroImages]
            }
            slideorvideo={heroSlideOrVideo}
            videourl={heroVideoUrl}
            title={heroTitle}
            description={heroDes}
            subTitle={heroSubTitle}
            buttonText={heroButtonText}
            watermark1={heroWatermark1}
            watermark2={heroWatermark2}
          />
        ),
        index: getSectionIndex("herosection", 1),
        shared: heroShared,
      });
    }

    // 2. STORY SECTION - 3 SPLIT SCREENS + SCREEN 4 GRID (SAME BASE SECTION INDEX)
    const storyShared = isSectionShared("cau-chuyen-thuong-hieu");
    const storyIndex = getSectionIndex("cau-chuyen-thuong-hieu", 2);

    if (storyShared) {
      dynamicStoryData.screens.forEach((screen, idx) => {
        list.push({
          id: `story-screen-${screen.id}`,
          label: "Câu chuyện",
          hideDotNav: false,
          transparent: false,
          element: <StorySplitSection data={screen} screenIndex={idx + 1} />,
          index: storyIndex + idx * 0.01,
          shared: storyShared,
        });
      });

      list.push({
        id: "story-screen-4",
        label: "Câu chuyện",
        hideDotNav: false,
        transparent: false,
        element: <StoryGridSection data={dynamicStoryData.screen4} />,
        index: storyIndex + 0.04,
        shared: storyShared,
      });
    }

    // 3. PROJECT OVERVIEW SECTION
    const overviewShared =
      isSectionShared("tong-quan-du-an") || isSectionShared("tong-quan");
    if (overviewShared) {
      list.push({
        id: "tong-quan-du-an",
        label: "Tổng quan",
        hideDotNav: false,
        transparent: false,
        element: <ProjectOverviewSection data={dynamicOverviewData} />,
        index: getSectionIndex(
          "tong-quan-du-an",
          getSectionIndex("tong-quan", 3),
        ),
        shared: overviewShared,
      });
    }

    // 4. FAMILY WELLNESS SECTION
    const wellnessShared =
      isSectionShared("he-sinh-thai-song-khoe") ||
      isSectionShared("gia-dinh-song-khoe");
    if (wellnessShared) {
      list.push({
        id: "gia-dinh-song-khoe",
        label: "Sống khỏe",
        hideDotNav: false,
        transparent: false,
        element: <FamilyWellnessSection data={dynamicFamilyWellnessData} />,
        index: getSectionIndex(
          "he-sinh-thai-song-khoe",
          getSectionIndex("gia-dinh-song-khoe", 4),
        ),
        shared: wellnessShared,
      });
    }
    // 5. LOCATION SECTION
    const locationShared = isSectionShared("vi-tri-ket-noi");
    if (locationShared) {
      list.push({
        id: "vi-tri-ket-noi",
        label: "Vị trí",
        hideDotNav: false,
        transparent: false,
        element: <LocationHighlightSection data={dynamicLocationData} />,
        index: getSectionIndex("vi-tri-ket-noi", 5),
        shared: locationShared,
      });
    }

    // 6. DAY IN LIFE SECTION
    const dayShared = isSectionShared("mot-ngay-song");
    if (dayShared && dynamicDayData) {
      list.push({
        id: "mot-ngay-song",
        label: "Một ngày sống",
        hideDotNav: false,
        transparent: false,
        element: <DayInLifeSection data={dynamicDayData} />,
        index: getSectionIndex("mot-ngay-song", 6),
        shared: dayShared,
      });
    }
    // 7. APARTMENT SPACES SECTION
    const spacesShared = isSectionShared("khong-gian-song");
    const spacesDataToUse = dynamicApartmentSpacesData || apartmentSpacesData;
    if (spacesShared && spacesDataToUse) {
      list.push({
        id: "khong-gian-song",
        label: "Không gian sống",
        hideDotNav: false,
        transparent: false,
        element: <ApartmentSpacesSection data={spacesDataToUse} />,
        index: getSectionIndex("khong-gian-song", 7),
        shared: spacesShared,
      });
    }
    // 6. SETTLEMENT SOLUTION SECTION
    const solutionShared = isSectionShared("giai-phap-an-cu");
    if (solutionShared && dynamicSettlementSolutionData) {
      list.push({
        id: "giai-phap-an-cu",
        label: "Giải pháp an cư",
        hideDotNav: false,
        transparent: false,
        element: (
          <SettlementSolutionSection data={dynamicSettlementSolutionData} />
        ),
        index: getSectionIndex("giai-phap-an-cu", 8),
        shared: solutionShared,
      });
    }

    // 7. CONSTRUCTION PROGRESS SECTION
    const progressShared = isSectionShared("tien-do-du-an");
    if (progressShared && dynamicConstructionProgressData) {
      list.push({
        id: "tien-do-du-an",
        label: "Tiến độ dự án",
        hideDotNav: false,
        transparent: false,
        element: (
          <ConstructionProgressSection data={dynamicConstructionProgressData} />
        ),
        index: getSectionIndex("tien-do-du-an", 9),
        shared: progressShared,
      });
    }

    // 9. GALLERY SECTION
    const galleryShared = isSectionShared("thu-vien");
    if (galleryShared && dynamicGalleryData) {
      list.push({
        id: "thu-vien",
        label: "Thư viện",
        hideDotNav: false,
        transparent: false,
        element: <GallerySection data={dynamicGalleryData} />,
        index: getSectionIndex("thu-vien", 9.5),
        shared: galleryShared,
      });
    }

    // 9.8 NEWS SECTION
    const newsShared =
      isSectionShared("tin-tuc") || isSectionShared("news-section");
    if (newsShared) {
      list.push({
        id: "tin-tuc",
        label: "Tin tức",
        hideDotNav: false,
        transparent: false,
        element: <NewsSection locale={locale} data={dynamicNewsData} />,
        index: getSectionIndex(
          "tin-tuc",
          getSectionIndex("news-section", 9.8),
        ),
        shared: newsShared,
      });
    }

    // 10. FOOTER CONTACT SECTION
    const footerShared =
      isSectionShared("footer") || isSectionShared("dang-ky-bao-gia");
    if (footerShared) {
      list.push({
        id: "dang-ky-bao-gia",
        label: "Liên hệ",
        hideDotNav: false,
        transparent: false,
        element: <FooterContact data={dynamicFooterContactData} />,
        index: getSectionIndex("footer", 10),
        shared: footerShared,
      });
    }

    return list.filter((s) => s.shared).sort((a, b) => a.index - b.index);
  }, [
    allAddOns,
    heroImages,
    heroSlidePictures,
    heroSlideOrVideo,
    heroVideoUrl,
    heroTitle,
    heroDes,
    heroSubTitle,
    heroButtonText,
    heroWatermark1,
    heroWatermark2,
    dynamicStoryData,
    dynamicOverviewData,
    dynamicFamilyWellnessData,
    dynamicNewsData,
    locale,
  ]);
  //#endregion

  const responsive = useResponsive();
  const { isDesktop } = responsive;

  const { activeIndex, scrollToSection } = useFullPageScroll({
    sectionsCount: activeSections.length,
    duration: 700,
  });

  return (
    <FullPageContext.Provider
      value={{
        activeIndex,
        scrollToSection,
        sectionsCount: activeSections.length,
        sections: activeSections,
        isFullPage: isDesktop,
      }}
    >
      <div className="relative w-full overflow-x-hidden">
        <ScrollHeader
          visibleSections={{
            "#banner-anchor": isSectionShared("herosection"),
            "#tin-tuc":
              isSectionShared("news-section") || isSectionShared("tin-tuc"),
            "#dang-ky-bao-gia":
              isSectionShared("footer") || isSectionShared("dang-ky-bao-gia"),
          }}
        />

        {/* FULL PAGE SCROLL VIEW FOR DESKTOP */}
        {isDesktop ? (
          <div className="h-screen w-full overflow-hidden relative">
            {/* CONTAINER SLIDE TRANSITION */}
            <div
              className="w-full h-full transition-transform duration-700 ease-in-out"
              style={{
                transform: `translateY(-${activeIndex * 100}vh)`,
              }}
            >
              {activeSections.map((sec) => (
                <div
                  key={sec.id}
                  id={sec.id}
                  className="w-full h-screen overflow-y-auto overflow-x-hidden relative flex-shrink-0"
                >
                  {sec.element}
                </div>
              ))}
            </div>
            <RightSidebar />
          </div>
        ) : (
          /* REGULAR PAGE SCROLL FOR MOBILE / TABLET */
          <div className="w-full">
            {activeSections.map((sec) => (
              <div key={sec.id} id={sec.id} className="w-full">
                {sec.element}
              </div>
            ))}
          </div>
        )}
      </div>
    </FullPageContext.Provider>
  );
}
